import {
  repoMap,
  frameworks,
  MANN_WHITNEY_U,
  DEFAULT_FRAMEWORK_ID,
} from '../../common/constants';
import { fetchSubtestsCompareResults } from '../../logic/treeherder';
import { Repository } from '../../types/state';
import { Framework, TestVersion } from '../../types/types';

// This function checks and sanitizes the input values, then returns values that
// we can then use in the rest of the application.
function checkValues({
  baseRev,
  baseRepo,
  newRev,
  newRepo,
  framework,
  baseParentSignature,
  newParentSignature,
  replicates,
  testVersion,
}: {
  baseRev: string | null;
  baseRepo: Repository['name'] | null;
  newRev: string | null;
  newRepo: Repository['name'] | null;
  framework: string | number | null;
  baseParentSignature: string | null;
  newParentSignature: string | null;
  replicates: boolean;
  testVersion?: TestVersion;
}): {
  baseRev: string;
  baseRepo: Repository['name'];
  newRev: string;
  newRepo: Repository['name'];
  frameworkId: Framework['id'];
  frameworkName: Framework['name'];
  baseParentSignature: string;
  newParentSignature: string;
  replicates: boolean;
  testVersion?: TestVersion;
} {
  if (baseRev === null) {
    throw new Error('The parameter baseRev is missing.');
  }

  if (baseRepo === null) {
    throw new Error('The parameter baseRepo is missing.');
  }

  if (newRev === null) {
    throw new Error('The parameter newRev is missing.');
  }

  if (newRepo === null) {
    throw new Error('The parameter newRepo is missing.');
  }

  const validRepoValues = Object.values(repoMap);
  if (!validRepoValues.includes(baseRepo)) {
    throw new Error(
      `The parameter baseRepo "${baseRepo}" should be one of ${validRepoValues.join(
        ', ',
      )}.`,
    );
  }
  if (!validRepoValues.includes(newRepo)) {
    throw new Error(
      `The parameter newRepo "${newRepo}" should be one of ${validRepoValues.join(
        ', ',
      )}.`,
    );
  }

  if (baseParentSignature === null) {
    throw new Error('The parameter baseParentSignature is missing.');
  }

  if (newParentSignature === null) {
    throw new Error('The parameter newParentSignature is missing.');
  }

  if (framework === null) {
    // Default to the default framework (talos) so that manually typing the URL
    // is easier.
    framework = DEFAULT_FRAMEWORK_ID;
  }

  const frameworkId = +framework as Framework['id'];
  if (Number.isNaN(frameworkId)) {
    throw new Error(
      `The parameter framework should be a number, but it is "${framework}".`,
    );
  }
  const frameworkName = frameworks.find(
    (entry) => entry.id === frameworkId,
  )?.name;

  if (!frameworkName) {
    throw new Error(
      `The parameter framework isn't a valid value: "${framework}".`,
    );
  }

  if (testVersion === MANN_WHITNEY_U || testVersion === null) {
    replicates = true;
  }

  if (!testVersion) {
    testVersion = MANN_WHITNEY_U;
  }

  return {
    baseRev,
    baseRepo,
    newRev,
    newRepo,
    frameworkId,
    frameworkName,
    baseParentSignature,
    newParentSignature,
    replicates,
    testVersion,
  };
}

// This function is responsible for fetching the data from the URL. It's called
// by React Router DOM when the compare-results path is requested.
// It uses the URL parameters as inputs, and returns all the fetched data to the
// React components through React Router's useLoaderData hook.
export function loader({ request }: { request: Request }) {
  const url = new URL(request.url);

  const baseRevFromUrl = url.searchParams.get('baseRev');
  const baseRepoFromUrl = url.searchParams.get('baseRepo') as
    | Repository['name']
    | null;
  const newRevFromUrl = url.searchParams.get('newRev');
  const newRepoFromUrl = url.searchParams.get('newRepo') as
    | Repository['name']
    | null;
  const frameworkFromUrl = url.searchParams.get('framework');
  const baseParentSignatureFromUrl = url.searchParams.get(
    'baseParentSignature',
  );
  const newParentSignatureFromUrl = url.searchParams.get('newParentSignature');
  const replicatesFromUrl = url.searchParams.has('replicates');
  const testVersionFromUrl = (url.searchParams.get('test_version') ??
    MANN_WHITNEY_U) as TestVersion;

  const {
    baseRev,
    baseRepo,
    newRev,
    newRepo,
    frameworkId,
    frameworkName,
    newParentSignature,
    baseParentSignature,
    replicates,
    testVersion,
  } = checkValues({
    baseRev: baseRevFromUrl,
    baseRepo: baseRepoFromUrl,
    newRev: newRevFromUrl,
    newRepo: newRepoFromUrl,
    framework: frameworkFromUrl,
    baseParentSignature: baseParentSignatureFromUrl,
    newParentSignature: newParentSignatureFromUrl,
    replicates: replicatesFromUrl,
    testVersion: testVersionFromUrl,
  });

  const results = fetchSubtestsCompareResults({
    baseRev,
    baseRepo,
    newRev,
    newRepo,
    framework: frameworkId,
    baseParentSignature,
    newParentSignature,
    replicates,
    testVersion,
  });

  return {
    results,
    baseRev,
    baseRepo,
    newRev,
    newRepo,
    frameworkId,
    frameworkName,
    baseParentSignature,
    newParentSignature,
    replicates,
    testVersion,
  };
}

export type LoaderReturnValue = Awaited<ReturnType<typeof loader>>;
